import type {
  Equipment,
  WellProperties,
  XrefPumps
} from "@/composables/forms/formTypes";
import type { MotorData } from "@/composables/scenarioCalculations/calculationTypes";
import type { Maybe } from "graphql/jsutils/Maybe";
import { API, SpecificGravity } from "../../units/density";
import type { BasicData } from "@/libs/calculations/fluidProperties/d";
import type { FluidProperties } from "../fluidProperties/d";
import { getMotorVoltageCorrections } from "./voltageCorrections";
import {
  getMotorCurrentA,
  getMotorEfficiencyRatio,
  getMotorPowerFactor,
  getMotorSpeedRPM
} from "./electrical";
import type { MotorCoefRR, MotorCoefInner } from "./types";
import {
  getMotorWindingOperatingTemperatureBaseF,
  getMotorSkinTemperatureF
} from "./temperatures";
import {
  getLegacyMotorSkinTemperatureF,
  getLegacyMotorWindingOperatingTemperatureF
} from "./legacyTemperatures";

export function getMotorData(
  basicData: BasicData,
  fluidProperties: FluidProperties,
  motor: NonNullable<Equipment>["r_motor"],
  xrefPump: Maybe<XrefPumps[number]>,
  pumpEfficiency: number,
  pumpHz: number,
  averageViscosityCP: number,
  shroudDiameterInches: number,
  topCasing: NonNullable<WellProperties>["r_top_casing"],
  topCasingDepthFt: NonNullable<WellProperties>["top_casing_length_feet"],
  bottomCasing: NonNullable<WellProperties>["r_bottom_casing"],
  legacyMotorLoadRatio: number,
  reratedMotorNameplateHP: number,
  reratedMotorNameplateV: number,
  motorNameplateA: number,
  motorRerate: number,
  pumpDynamicHeadFt: number,
  totalRequired60HzBrakeHP: number,
  fluidDensityInlet: number,
  fluidDensityOutlet: number,
  downholeMotorTemperatureF: number
): MotorData {
  const result = {
    internal_temperature: 0,
    skin_temperature: 0,
    flow_velocity_around_motor: 0,
    motor_current: 0,
    motor_efficiency: 0,
    motor_power_factor: 0,
    motor_voltage: 0,
    motorLoadRatioNP: 0,
    motorLoadRatioRR: 0,
    motorRatedRPM: 0,
    baseWindingOperatingTemp: 0,
    motorVoltageCorrections: {
      optimizedVolts: 0,
      optimizedVoltRatio: 0,
      ampCorrection: 1,
      powerFactorCorrection: 1,
      efficiencyCorrection: 1,
      rpmCorrection: 1,
      tempCorrection: 1
    }
  };
  if (
    !(
      motor?.nameplate_voltage &&
      motor?.nameplate_horsepower &&
      motor?.nameplate_current
    )
  ) {
    return result;
  }
  // Flow velocity around motor
  let internalDiameterIn = topCasing?.interior_diameter_inches || 0;

  if (bottomCasing && basicData.bottomOfPumpsDepthFt >= topCasingDepthFt) {
    internalDiameterIn = bottomCasing.interior_diameter_inches;
  }

  const diameterForFlowVelocityIn = shroudDiameterInches || internalDiameterIn;

  result.flow_velocity_around_motor =
    fluidProperties.liquidSuperficialVelocityFTSEC;

  // Power calculations
  result.motorLoadRatioRR = totalRequired60HzBrakeHP / reratedMotorNameplateHP;
  result.motorLoadRatioNP =
    totalRequired60HzBrakeHP / motor.nameplate_horsepower;

  result.motor_voltage = (reratedMotorNameplateV * pumpHz) / 60;

  result.motorVoltageCorrections = getMotorVoltageCorrections(
    motor,
    result.motorLoadRatioNP,
    pumpHz,
    result.motor_voltage
  );

  const {
    ampCorrection,
    powerFactorCorrection,
    efficiencyCorrection,
    rpmCorrection,
    tempCorrection
  } = result.motorVoltageCorrections;

  const motor_current = getMotorCurrentA(
    result.motorLoadRatioNP,
    motorNameplateA,
    motor.ib,
    motor.i1,
    motor.i2,
    motor.i3,
    motor.i4,
    motor.i5,
    ampCorrection
  );

  result.motor_current = motor_current;

  const motor_eff = getMotorEfficiencyRatio(
    result.motorLoadRatioNP,
    motor.effb,
    motor.eff1,
    motor.eff2,
    motor.eff3,
    motor.eff4,
    motor.eff5,
    efficiencyCorrection
  );

  result.motor_efficiency = motor_eff;

  const motor_pfactor = getMotorPowerFactor(
    result.motorLoadRatioNP,
    motor.pfb,
    motor.pf1,
    motor.pf2,
    motor.pf3,
    motor.pf4,
    motor.pf5,
    powerFactorCorrection
  );

  result.motor_power_factor = motor_pfactor;

  result.motorRatedRPM = getMotorSpeedRPM(
    1,
    60,
    motor.nb,
    motor.n1,
    motor.n2,
    motor.n3,
    motor.n4,
    motor.n5,
    rpmCorrection
  );

  const fullDepth = basicData.bottomOfPumpsDepthFt;

  let casingOrShroudInternalDiameterInches =
    shroudDiameterInches || topCasing?.interior_diameter_inches;

  if (bottomCasing && fullDepth >= topCasingDepthFt) {
    casingOrShroudInternalDiameterInches =
      shroudDiameterInches || bottomCasing?.interior_diameter_inches;
  }

  const pump = xrefPump?.r_pump;

  const oilSpecifcGravity = SpecificGravity.toThisUnit(
    API.toStandardUnit(basicData.oilAPI)
  );

  const rrl0 = motor.rr_lb;
  const rrl1 = motor.rr_l1;
  const rrl2 = motor.rr_l2;

  const motorCoefRR: MotorCoefRR = {
    rrl0,
    rrl1,
    rrl2
  };
  const baseWindingOperatingTemp = getMotorWindingOperatingTemperatureBaseF(
    downholeMotorTemperatureF,
    result.motorLoadRatioNP,
    motorCoefRR
  );

  result.baseWindingOperatingTemp = baseWindingOperatingTemp;

  if (
    motor.manufacturer?.toLowerCase().includes("summit") &&
    !motor.deleted_at &&
    pump
  ) {
    const partNumber = motor.part_number;
    const outerDiameterInches = motor.outer_diameter_inches;
    const rrl0 = motor.rr_lb;
    const rrl1 = motor.rr_l1;
    const rrl2 = motor.rr_l2;
    const statorID = motor.stator_inner_diameter_inches;
    const statorOD = motor.stator_outer_diameter_inches;
    const k3 = motor.k3;
    const nameplateRerate = motor.nameplate_rerate_ratio;
    const motorCoefInner: MotorCoefInner = {
      partNumber,
      outerDiameterInches,
      rrl0,
      rrl1,
      rrl2,
      statorID,
      statorOD,
      k3,
      nameplateRerate
    };

    result.skin_temperature = getMotorSkinTemperatureF(
      motor.length_inches,
      diameterForFlowVelocityIn,
      downholeMotorTemperatureF,
      fluidProperties.mixSuperficialVelocityFTSEC,
      fluidProperties.gasSuperficialVelocityFTSEC,
      fluidProperties.liquidSuperficialVelocityFTSEC,
      fluidProperties.waterCutRatio,
      fluidProperties.gasDensityLBMFT3,
      fluidProperties.liquidDensityLBMFT3,
      baseWindingOperatingTemp,
      fluidProperties.liquidSpecificHeatCapacity,
      fluidProperties.fluidViscosityCP,
      fluidProperties.liquidViscosityCP,
      motorCoefInner,
      fluidProperties.equipmentCrossSectionFt2
    );
    const motorCorrMotorLoad =
      (baseWindingOperatingTemp - downholeMotorTemperatureF) * tempCorrection;
    result.internal_temperature = result.skin_temperature + motorCorrMotorLoad;
  } else {
    // Use legacy calculations for non-Summit motors
    if (pump) {
      const sktemp = getLegacyMotorSkinTemperatureF(
        motor.outer_diameter_inches,
        motor.length_inches,
        motor.nameplate_horsepower,
        motorRerate,
        legacyMotorLoadRatio,
        motor_eff,
        pumpEfficiency,
        casingOrShroudInternalDiameterInches,
        averageViscosityCP,
        fluidProperties.waterCutRatio,
        fluidProperties.liquidFlowRateBBLD,
        pumpDynamicHeadFt,
        oilSpecifcGravity,
        basicData.waterSpecificGravity,
        basicData.bottomholeTemperatureF,
        fluidDensityInlet ?? 0,
        fluidDensityOutlet ?? 0
      );

      result.skin_temperature = sktemp;

      if (topCasing && motor) {
        const internal_temp = getLegacyMotorWindingOperatingTemperatureF(
          motor.outer_diameter_inches,
          motor.length_inches,
          motor.nameplate_horsepower,
          motorRerate,
          legacyMotorLoadRatio,
          motor_eff,
          pumpEfficiency,
          casingOrShroudInternalDiameterInches,
          averageViscosityCP,
          fluidProperties.waterCutRatio,
          fluidProperties.liquidFlowRateBBLD,
          pumpDynamicHeadFt,
          oilSpecifcGravity,
          basicData.waterSpecificGravity,
          basicData.bottomholeTemperatureF,
          fluidDensityInlet ?? 0,
          fluidDensityOutlet ?? 0
        );
        result.internal_temperature = internal_temp;
      }
    }
  }

  return result;
}
